import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { useDashboardDateRange } from "../../../../context/DashboardDateRangeContext";
import { Tabs } from "ui/components/Tabs";
import { Loader } from "ui/components/Loader";
import {
    getVehicleStatsBrandsAPI,
    getVehicleStatsFuelAPI,
    getVehicleStatsMarginAPI,
    getVehicleStatsPriceCompareAPI,
} from "services/api/dashboard/api";
import { VehicleStatsTabProps } from "./types";
import VehicleFuelDistributionCard from "./components/VehicleFuelDistributionCard";
import VehicleMarginCard from "./components/VehicleMarginCard";
import VehiclePriceCompareCard from "./components/VehiclePriceCompareCard";
import VehicleStatusCard from "./components/VehicleStatusCard";

interface SlideContentProps<T> {
    fetch: () => Promise<T>;
    render: (data: T) => React.ReactNode;
}

function SlideContent<T>({ fetch, render }: SlideContentProps<T>) {
    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        (async () => {
            try {
                const result = await fetch();
                if (!cancelled) setData(result);
            } catch {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [fetch]);

    if (isLoading) {
        return (
            <div className="relative flex items-center justify-center h-[350px]">
                <Loader isLoading={true}> </Loader>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex items-center justify-center h-[350px] text-sm text-slate-400">
                Nema podataka
            </div>
        );
    }

    return <>{render(data)}</>;
}

const VehicleStatsTab: React.FC<VehicleStatsTabProps> = () => {
    const { t } = useTranslate();
    const { range } = useDashboardDateRange();

    // Marža i Usporedba cijena reagiraju na globalni datumski filter; Marke i Gorivo su snapshot flote.
    const fetchMargin = useCallback(() => getVehicleStatsMarginAPI(range.from, range.to), [range.from, range.to]);
    const fetchPriceCompare = useCallback(() => getVehicleStatsPriceCompareAPI(range.from, range.to), [range.from, range.to]);

    return (
        <div className="p-2 my-4 ml-4">
            <Tabs
                variant="pill"
                tabs={[
                    {
                        label: "Marke",
                        content: (
                            <div className="min-h-[380px] pt-2">
                                <SlideContent
                                    fetch={getVehicleStatsBrandsAPI}
                                    render={(data) => <VehicleStatusCard data={data} />}
                                />
                            </div>
                        ),
                    },
                    {
                        label: "Marža",
                        content: (
                            <div className="min-h-[380px] pt-2">
                                <SlideContent
                                    fetch={fetchMargin}
                                    render={(data) => <VehicleMarginCard data={data} />}
                                />
                            </div>
                        ),
                    },
                    {
                        label: "Usporedba cijena",
                        content: (
                            <div className="min-h-[380px] pt-2">
                                <SlideContent
                                    fetch={fetchPriceCompare}
                                    render={(data) => <VehiclePriceCompareCard data={data} />}
                                />
                            </div>
                        ),
                    },
                    {
                        label: "Gorivo",
                        content: (
                            <div className="min-h-[380px] pt-2">
                                <SlideContent
                                    fetch={getVehicleStatsFuelAPI}
                                    render={(data) => <VehicleFuelDistributionCard data={data} />}
                                />
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
};

export default memo(VehicleStatsTab);
